<template>
  <div class="cb-custom-data-table-raw" :style="componentStyle">
    <!-- Custom Header -->
    <div v-if="headerHtml" class="p-4 bg-base">
      <div v-trusted-html="headerHtml"></div>
    </div>

    <template v-if="flattenedRows.length">
      <div class="cb-custom-data-table-raw__scroll-container">
        <table class="cb-custom-data-table-raw__table">
          <TableHeader
            :columns="columns"
            :current-sorting="currentSorting"
            :header-background-color="headerBackgroundColor"
            :header-foreground-color="headerForegroundColor"
            @sort="handleSort"
          />
          <!-- Each tbody wraps a logical group to enable page-break-inside: avoid -->
          <tbody v-for="chunk in rowChunks" :key="chunk.id" :class="{ 'print-no-break': chunk.isInnermostGroupChunk }">
            <template v-for="row in chunk.rows" :key="row.id">
              <!-- Group Row -->
              <tr
                v-if="row.type === 'group'"
                class="cb-custom-data-table-group-row cursor-pointer hover:opacity-90"
                :style="{
                  backgroundColor: groupHeaderBackgroundColor,
                  color: getContrastTextColor(groupHeaderBackgroundColor),
                }"
                @click="handleRowToggle(row.id)"
              >
                <td
                  class="p-3 px-4 font-600 whitespace-nowrap"
                  :colspan="columns.length"
                  :style="{ paddingLeft: `${row.level * 24 + 16}px` }"
                >
                  <div class="flex items-center gap-2">
                    <CIcon class="cursor-pointer" :icon="row.isExpanded ? 'chevron-down' : 'chevron-right'" size="16" />
                    <span v-trusted-html="row.groupValue" class="font-medium"></span>
                  </div>
                </td>
              </tr>
              <!-- Data Row -->
              <tr
                v-else-if="row.type === 'data'"
                class="cb-custom-data-table-row hover:opacity-95"
                :style="{ backgroundColor: getRowBackgroundColor(dataRowGlobalIndexMap.get(row.id) ?? 0) }"
              >
                <td
                  v-for="(col, colIdx) in columns"
                  :key="col.id"
                  class="py-[10px] px-4 whitespace-nowrap min-w-[120px]"
                  :style="{
                    paddingLeft: colIdx === 0 && row.level > 0 ? `${row.level * 24 + 16}px` : '16px',
                    borderBottom: '1px solid var(--primary5-1)',
                  }"
                >
                  <span v-trusted-html="formatCellValue(get(row.data, col.id), col.property)"></span>
                </td>
              </tr>
            </template>
          </tbody>
          <!-- Custom Footer -->
          <tfoot v-if="footerHtml">
            <tr
              class="bg-primary6"
              :style="{
                borderTop: `3px solid ${themeVars.primary5}`,
              }"
            >
              <td class="p-4 font-500" :colspan="columns.length">
                <div v-trusted-html="footerHtml"></div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
    <div v-else class="text-center p-4 text-primary3">
      {{ t('canvasUI.canvasBuilder.customDataTable.noData') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { get } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { type ColumnSort } from '@tanstack/table-core'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import TableHeader from './CustomDataTable.raw.header.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import { vTrustedHtml } from '@lib/directives/vTrustedHtml'

import { DEFAULT_FONT } from '@canvas-builder/constants'
import { type CustomDataTableProps, type DynamicDatatableProperty } from '@canvas-builder/types/canvas'
import { getContrastTextColor } from '@lib/util/color'
import { formatCellValue } from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.utils'
import {
  processHierarchicalData,
  flattenForRendering,
  toggleRowExpansion,
  type ProcessedHierarchicalRow,
  type RowWithAncestors,
} from './CustomDataTable.utils'

const props = defineProps<{
  data?: CustomDataTableProps
  style?: Record<string, any>
  id?: string
}>()

const emit = defineEmits<{
  sortChanged: [sorting: ColumnSort | null]
}>()

const { t } = useI18n()
const { activeCanvas, isPrintDisplayMode } = useCanvas()
const themeVars = useThemeVars()

const hierarchicalRows = ref<ProcessedHierarchicalRow[]>([])
const currentSorting = ref<ColumnSort | null>(null)

const componentStyle = computed(() => {
  return {
    ...props.style,
    fontFamily: fontFamily.value,
  }
})

const sourceData = computed(() => {
  return activeCanvas.value?.context || null
})

const fontFamily = computed(() => {
  return props.data?.theme?.font_family ?? DEFAULT_FONT
})

const headerBackgroundColor = computed(() => {
  return props.data?.theme?.background_color ?? themeVars.value.primary6
})

const groupHeaderBackgroundColor = computed(() => {
  return props.data?.theme?.group_header_color ?? themeVars.value.primary6
})

const headerForegroundColor = computed(() => {
  return getContrastTextColor(headerBackgroundColor.value)
})

const columns = computed(() => {
  if (!props.data?.columns) return []

  return props.data.columns.map((prop: DynamicDatatableProperty) => ({
    id: prop.key,
    header: prop.display_name || prop.key,
    property: prop,
    enableSorting: prop.sort?.enabled ?? false,
    sortDescFirst: prop.sort?.direction === 'desc',
  }))
})
// Process hierarchical data
const processedData = computed(() => {
  if (!sourceData.value || !props.data?.hierarchy?.length) return []

  // Convert currentSorting to sortConfig format
  const sortConfig = currentSorting.value
    ? { key: currentSorting.value.id, direction: currentSorting.value.desc ? ('desc' as const) : ('asc' as const) }
    : undefined

  return processHierarchicalData(sourceData.value, props.data.hierarchy, props.data.columns, sortConfig)
})

// Watch for changes in processed data and update hierarchical rows
watch(
  processedData,
  (newData) => {
    hierarchicalRows.value = newData
  },
  { immediate: true },
)

// Flatten for rendering
const flattenedRows = computed(() => {
  return flattenForRendering(hierarchicalRows.value, false, isPrintDisplayMode.value)
})

// Helper to check if a data row belongs to an innermost group
function isDataRowOfInnermostGroup(row: RowWithAncestors): boolean {
  if (row.type !== 'data' || !row.ancestorGroups) return false
  // Check if the immediate parent (last ancestor) is an innermost group
  const immediateParent = row.ancestorGroups[row.ancestorGroups.length - 1]
  return !!(immediateParent?.type === 'group' && immediateParent.children?.every((child) => child.type === 'data'))
}

// Group rows into chunks for tbody wrapping in print mode
// Each chunk represents either:
// - An innermost group + its data rows (for page-break prevention)
// - A single non-innermost group row
interface RowChunk {
  id: string
  rows: RowWithAncestors[]
  isInnermostGroupChunk: boolean
}

const rowChunks = computed((): RowChunk[] => {
  const chunks: RowChunk[] = []
  const rows = flattenedRows.value

  let i = 0
  while (i < rows.length) {
    const row = rows[i]

    if (row.type === 'group' && row.isInnermostGroup) {
      // Start a new chunk for innermost group + its data rows
      const chunkRows: RowWithAncestors[] = [row]
      i++

      // Collect all data rows that belong to this group
      while (i < rows.length && rows[i].type === 'data' && isDataRowOfInnermostGroup(rows[i])) {
        chunkRows.push(rows[i])
        i++
      }

      chunks.push({
        id: `chunk-${row.id}`,
        rows: chunkRows,
        isInnermostGroupChunk: true,
      })
    } else {
      // Single row chunk (non-innermost group or standalone data row)
      chunks.push({
        id: `chunk-${row.id}`,
        rows: [row],
        isInnermostGroupChunk: false,
      })
      i++
    }
  }

  return chunks
})

// Map each data row ID to its global index (for striping)
const dataRowGlobalIndexMap = computed(() => {
  const map = new Map<string, number>()
  let dataRowIndex = 0

  for (const row of flattenedRows.value) {
    if (row.type === 'data') {
      map.set(row.id, dataRowIndex)
      dataRowIndex++
    }
  }

  return map
})

// Get header HTML from data source
const headerHtml = computed(() => {
  if (!props.data?.header_path || !sourceData.value) return null

  try {
    const headerData = get(sourceData.value, props.data.header_path)
    if (!headerData) return null

    // If it's a string, return it directly
    if (typeof headerData === 'string') {
      return headerData
    }

    // If it's an object or array, convert to JSON string for display
    return JSON.stringify(headerData)
  } catch (error) {
    console.error('Error accessing header path:', error)
    return null
  }
})

// Get footer HTML from data source
const footerHtml = computed(() => {
  if (!props.data?.footer_path || !sourceData.value) return null

  try {
    const footerData = get(sourceData.value, props.data.footer_path)
    if (!footerData) return null

    // If it's a string, return it directly
    if (typeof footerData === 'string') {
      return footerData
    }

    // If it's an object or array, convert to JSON string for display
    return JSON.stringify(footerData)
  } catch (error) {
    console.error('Error accessing footer path:', error)
    return null
  }
})

function handleRowToggle(rowId: string) {
  hierarchicalRows.value = toggleRowExpansion(hierarchicalRows.value, rowId)
}

function handleSort(columnId: string) {
  if (currentSorting.value?.id === columnId) {
    // Cycle: asc → desc → null
    if (currentSorting.value.desc) {
      currentSorting.value = null
    } else {
      currentSorting.value = { id: columnId, desc: true }
    }
  } else {
    // New column: start with asc
    currentSorting.value = { id: columnId, desc: false }
  }

  // Emit the sort change event
  emit('sortChanged', currentSorting.value)
}

function getRowBackgroundColor(idx: number): string {
  const enableRowStriping = props.data?.enable_row_striping !== false

  if (!enableRowStriping) {
    return themeVars.value.base
  }

  const stripingStartFromFirst = props.data?.striping_start_from_first ?? false
  const shouldStripe = stripingStartFromFirst ? !!((idx + 1) % 2) : !!(idx % 2)

  if (shouldStripe) {
    return props.data?.striping_color ?? themeVars.value.primary6_1
  }

  return themeVars.value.base
}

// Initialize default sorting on mount
onMounted(() => {
  const defaultSortColumn = props.data?.columns?.find((col) => col.sort?.default && col.sort?.enabled)
  if (defaultSortColumn) {
    currentSorting.value = {
      id: defaultSortColumn.key,
      desc: defaultSortColumn.sort?.direction === 'desc',
    }
  }
})
</script>

<style lang="scss" scoped>
.cb-custom-data-table-raw {
  &__scroll-container {
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: var(--primary4) var(--primary6);

    // WebKit browsers (Chrome, Safari, Edge)
    &::-webkit-scrollbar {
      height: 12px;
      background: transparent;
    }

    &::-webkit-scrollbar-track {
      background: var(--primary6);
      border-radius: 6px;
      margin: 0 8px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--primary4);
      border-radius: 6px;
      border: 2px solid var(--primary6);

      &:hover {
        background: var(--primary3);
      }

      &:active {
        background: var(--primary2);
      }
    }

    &::-webkit-scrollbar-corner {
      background: transparent;
    }
  }

  &__table {
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }
}

@media print {
  .cb-custom-data-table-raw {
    page-break-before: always !important;
    break-before: always !important;
    max-width: 100% !important;
    overflow: visible !important;

    &__scroll-container {
      overflow-x: visible !important;
      overflow-y: visible !important;
      scrollbar-width: none !important;
      max-width: 100% !important;

      &::-webkit-scrollbar {
        display: none !important;
      }
    }

    &__table {
      width: 100% !important;
      max-width: 100% !important;
      table-layout: auto !important;

      // Repeat table header on each page
      thead {
        display: table-header-group;
      }

      tfoot {
        display: table-footer-group;
      }

      // Prevent innermost groups (tbody) from splitting across pages
      tbody.print-no-break {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        page-break-before: auto !important;
        break-before: auto !important;
        page-break-after: auto !important;
        break-after: auto !important;
      }
    }

    :deep(th),
    :deep(td),
    :deep(tr) {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      min-width: 0 !important;
      max-width: none !important;
    }

    // Allow columns to shrink to fit
    :deep(th) {
      padding-top: 8px !important;
      padding-bottom: 8px !important;
      padding-right: 4px !important;
      font-size: 11px !important;
    }

    :deep(td) {
      padding-top: 6px !important;
      padding-bottom: 6px !important;
      padding-right: 4px !important;
      font-size: 10px !important;
    }
  }
}
</style>
